import {Select} from '@dropbox/dig-components/text_fields';
import {Box} from '@dropbox/dig-foundations';
import {Workstream} from 'client';
import {t} from 'i18next';
import {useEffect, useMemo, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {FadeIn, useHasMounted} from 'views/workstreams/page/FadeIn';
import {type ActivityViewType, isActivityViewType} from 'views/workstreams/types';
import {WorkstreamAuditLog} from 'views/workstreams/WorkstreamAuditLog';

import styles from './WorkstreamDetailsShell.module.css';
import {SectionToggleButton} from './WorkstreamSectionCard';

const ACTIVITY_VIEW_OPTIONS: {label: string; value: ActivityViewType}[] = [
  {label: 'workstream_updates_only', value: 'updates'},
  {label: 'workstream_outcomes_only', value: 'outcomes'},
  {label: 'workstream_all_activity', value: 'all'},
];

export const WorkstreamActivitySection = ({
  isExpanded,
  onExpandedChange,
  requestedActivityViewType,
  requestedActivityViewTypeVersion,
  workstream,
  onUpdatesLoaded,
  steadyHeader = false,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  requestedActivityViewType?: ActivityViewType | null;
  requestedActivityViewTypeVersion?: number;
  workstream: Workstream;
  onUpdatesLoaded?: () => void;
  // Keep the header one height open or closed, with the other sections'
  // collapsed spacing (see .activityHeaderSteady).
  steadyHeader?: boolean;
}) => {
  const location = useLocation();
  const mounted = useHasMounted();
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const resolvedActivityViewType = useMemo<ActivityViewType>(() => {
    const updatesOnly =
      Boolean(location.state) &&
      typeof location.state === 'object' &&
      Boolean((location.state as {updatesOnly?: boolean}).updatesOnly);

    if (updatesOnly) {
      return 'updates';
    }

    return isActivityViewType(requestedView) ? requestedView : 'all';
  }, [location.state, requestedView]);
  const [activityViewType, setActivityViewType] =
    useState<ActivityViewType>(resolvedActivityViewType);

  useEffect(() => {
    setActivityViewType(resolvedActivityViewType);
  }, [location.key, resolvedActivityViewType]);

  useEffect(() => {
    if (!requestedActivityViewType) {
      return;
    }

    setActivityViewType(requestedActivityViewType);
  }, [requestedActivityViewType, requestedActivityViewTypeVersion]);

  return (
    <section
      id="activity"
      className={`${styles.activitySection} ${!isExpanded && !steadyHeader ? styles.activitySectionCollapsed : ''}`}
    >
      <div className={styles.activityContent}>
        <Box
          className={[
            styles.activityHeader,
            steadyHeader && styles.activityHeaderSteady,
            steadyHeader && !isExpanded && styles.activityHeaderCollapsed,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <SectionToggleButton
            fold="activity"
            title={t('activity')}
            isExpanded={isExpanded}
            onToggle={() => onExpandedChange(!isExpanded)}
            ariaLabel={isExpanded ? t('collapse_activity') : t('expand_activity')}
          />
          {isExpanded ? (
            <Box className={styles.activityFilter}>
              <Select
                id="workstream-activity-filter"
                size="small"
                value={activityViewType}
                onChange={(value) => setActivityViewType(value as ActivityViewType)}
              >
                {ACTIVITY_VIEW_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {t(option.label)}
                  </Select.Option>
                ))}
              </Select>
            </Box>
          ) : null}
        </Box>

        {isExpanded ? (
          // Fades in when opened after the page arrived.
          <FadeIn enabled={mounted}>
            <WorkstreamAuditLog
              workstream={workstream}
              withUpdates
              oldestFirst={false}
              activityViewType={activityViewType}
              onUpdatesLoaded={onUpdatesLoaded}
            />
          </FadeIn>
        ) : null}
      </div>
    </section>
  );
};
